/**
 * Commandes - Liste des commandes pour les prestataires
 * 
 * Version refactorisée utilisant les modules:
 * - types.ts: Interfaces et types
 * - statusConfig.ts: Configuration des onglets et statuts
 * - utils.ts: Fonctions utilitaires
 */
import './Commandes.css';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Icons importés comme composants SVG
let PlusIcon: any = null;
let ExportIcon: any = null; 
let FilterIcon: any = null;
try {
  PlusIcon = require('../../assets/icons/plus.svg').ReactComponent;
  ExportIcon = require('../../assets/icons/export.svg').ReactComponent;
  FilterIcon = require('../../assets/icons/filter.svg').ReactComponent;
} catch (e) {
  // Icons fallback si non disponibles
}
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole, isAdminRole } from '../../utils/roleUtils';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV } from '../../utils/commonHandlers';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import Button from '../../composants/Button/Button';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { formatDate, formatPrice } from '../../utils/entityUtils';

// Imports depuis les modules refactorisés
import { Commande, ApiResponse, PaginationState } from './types';
import { TABS, TAB_TO_STATUS_MAPPING, TYPE_OPTIONS } from './statusConfig';
import {
  toCssClass,
  getUserRoleFromStorage,
  getStatusActionMapping,
  transformCommandesData,
  sortPaniersImportesByPriority
} from './utils';
import {
  FilterOptions,
  transformFiltersToApiParams,
  buildQueryString,
  mergeFiltersWithBaseParams
} from './api';

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

function Commandes({ onNotificationUpdate }: { onNotificationUpdate?: (count: number) => void }) {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const navigate = useNavigate();

  // États
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(false);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(false);
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [filteredData, setFilteredData] = useState<Commande[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [unreadCommandes, setUnreadLocalCommandes] = useState<number>(0);
  const [dataArchive, setDataArchive] = useState<[]>([]);
  const [paniersImportesCount, setPaniersImportesCount] = useState<number>(0);
  const [actionRequisCount, setActionRequisCount] = useState<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // États pour la suppression de BCI
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [commandeToDelete, setCommandeToDelete] = useState<Commande | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Contexte de notifications
  const { setUnreadCommandes } = useNotifications();

  // Onglet actif - 1 = 'Action requise' par défaut
  const [activeTab, setActiveTab] = useState(0);
  const [selectedType, setSelectedType] = useState<string>(TYPE_OPTIONS[0].value);

  // État de pagination
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    lastPage: 1,
    perPage: 100,
    total: 0
  });

  // État des filtres actifs
  const [activeFilters, setActiveFilters] = useState<Partial<FilterOptions>>({});

  // ============================================================================
  // HANDLERS - ONGLETS ET TYPE
  // ============================================================================

  const handleOpenLancerMarcheModal = useCallback(() => {
    setIsLancerMarcheModalOpen(true);
  }, []);

  const handleTabChange = useCallback((tabIndex: number) => {
    if (tabIndex === activeTab) return;

    // Réinitialiser les états - le useEffect déclenchera le fetch
    setLoading(true);
    setError(null);
    setCommandes([]);
    setFilteredData([]);
    setDataArchive([]);
    setCurrentPage(1);
    setActiveTab(tabIndex);
    // Réinitialiser les filtres lors du changement d'onglet
    setActiveFilters({});
  }, [activeTab]);

  const handleTypeChange = useCallback((value: string) => {
    setSelectedType(value);
    // Le useEffect déclenchera le fetch grâce à la dépendance selectedType
  }, []);

  // ============================================================================
  // HANDLERS - NAVIGATION
  // ============================================================================

  const handleRowClick = useCallback((id: string) => {
    const commande = commandes.find(c => c.id === id);
    if (!commande) return;

    let action = '';
    let tab = 0;

    // Logique de redirection basée sur le statut
    if (commande.status.nom === 'Brouillon') {
      action = 'reprendre';
      tab = 0;
    } else if (['En attente de l\'analyse', 'En analyse'].includes(commande.status.nom)) {
      action = '';
      tab = 1;
    } else if (commande.source === 'BC' && commande.status.id === 11) {
      action = 'chiffrer';
      tab = 2;
    } else if (commande.source === 'BC' && (commande.status.id === 13 || commande.status.nom === "En attente de décision de l'acheteur")) {
      action = 'view';
      tab = 2;
    } else if (commande.source === 'BCI' && commande.status.id === 11) {
      action = 'valider';
      tab = 0;
    } else if (commande.source === 'BCI') {
      // Pour toutes les BCI, rediriger vers l'onglet 0
      action = 'view';
      tab = 0;
    } else if (commande.status.id >= 8 && commande.status.id < 12) {
      action = 'signer';
      tab = 2;
    } else if (commande.status.nom === 'À candidater') {
      action = 'candidater';
      tab = 1;
    } else if (commande.status.nom === 'À accepter') {
      action = 'accepter';
      tab = 2;
    } else {
      action = 'view';
      tab = 0;
    }

    const basePath =
      commande.source?.toLowerCase().trim() === 'bc'
        ? '/commande-bc'
        : commande.source?.toLowerCase().trim() === 'bci'
          ? '/commande-bci'
          : '/commande-ms';

    navigate(`${basePath}/${id}?tab=${tab}`, { state: { action } });
  }, [commandes, navigate]);

  // Actions spécifiques - mémoisées pour éviter les re-renders
  const handleCandidater = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=1`, { state: { action: 'candidater' } }), [navigate]);
  const handleAnalyser = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=2`, { state: { action: 'analyser' } }), [navigate]);
  const handleReprendre = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=1`, { state: { action: 'reprendre' } }), [navigate]);
  const handleSigner = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=2`, { state: { action: 'signer' } }), [navigate]);
  const handleAccepter = useCallback((id: string) => navigate(`/commande-bc/${id}?tab=2`, { state: { action: 'accepter' } }), [navigate]);
  const handleChiffrer = useCallback((id: string) => navigate(`/commande-bc/${id}?tab=2`, { state: { action: 'chiffrer' } }), [navigate]);

  const handleValider = useCallback((row: Commande) => {
    if (!row.id || row.source !== 'BCI') return;
    navigate(`/commande-bci/${row.id}?tab=0`, { state: { action: 'valider' } });
  }, [navigate]);

  const handleExport = useCallback((id: string) => {
    const commandeToExport = commandes.find(cmd => cmd.id.toString() === id);
    if (commandeToExport) {
      // Note: columns sera défini au moment de l'exécution, pas besoin dans les dépendances
      handleExportCSV([commandeToExport], [], `commande-${id}-${new Date().toISOString().slice(0, 10)}.csv`);
    }
  }, [commandes]);

  // ============================================================================
  // HANDLERS - TÉLÉCHARGEMENTS
  // ============================================================================

  const handleDownload = useCallback(async (commande: Commande) => {
    try {
      // Formater le département sur 2 chiffres (ex: 6 -> "06")
      const departement = commande?.id_lot_departement?.toString().padStart(2, '0') || '';

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            external_id: commande?.source === 'BCI'
              ? `${departement}${commande?.operation.numero?.toString() || commande?.operation?.id?.toString()}F`
              : commande?.operation.numero?.toString() || commande?.operation?.id?.toString(),
            external_type: "operation",
            source: commande?.source || 'MS',
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du document signé');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const annee = process.env.REACT_APP_ANNEE_EDITION || "25";
      const numeroSansP = commande?.operation?.numero;
      a.download = `${annee}${numeroSansP} (signed).pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors du téléchargement du marché signé');
    }
  }, []);

  const downloadArchivePDF = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc-archive/${id}/download`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur téléchargement archive');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archive-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Impossible de télécharger le fichier PDF.');
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/commandes/exportCommandes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) throw new Error('Erreur lors de l\'exportation des commandes');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-commandes-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des commandes');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${id}/relancer-signataire`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la relance');

      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  // ============================================================================
  // HANDLERS - SUPPRESSION BCI
  // ============================================================================

  /**
   * Ouvre la popup de confirmation pour supprimer une commande BCI
   */
  const handleDeleteBCI = useCallback((commande: Commande) => {
    setCommandeToDelete(commande);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Confirme la suppression de la commande BCI
   */
  const handleConfirmDelete = async () => {
    if (!commandeToDelete || isDeleting) {
      setIsDeleteModalOpen(false);
      return;
    }

    const commandeId = commandeToDelete.id.toString();
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/delete`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression de la commande');
      }

      // Retirer la commande de la liste locale
      setCommandes(prev => prev.filter(cmd => cmd.id.toString() !== commandeId));
      setFilteredData(prev => prev.filter(cmd => cmd.id.toString() !== commandeId));

      // Mettre à jour le total de la pagination
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));

      // Fermer la popup
      setIsDeleteModalOpen(false);
      setCommandeToDelete(null);
      setIsDeleting(false);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setDeleteError(error.message || 'Une erreur est survenue lors de la suppression de la commande.');
      setIsDeleting(false);
    }
  };

  /**
   * Annule la suppression de la commande BCI
   */
  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setCommandeToDelete(null);
    setDeleteError(null);
  }, []);

  // ============================================================================
  // HANDLERS - PAGINATION, FILTRES, RECHERCHE
  // ============================================================================

  const handlePageChange = useCallback((page: number) => {
    // Mettre à jour currentPage - le useEffect déclenchera le fetch
    setCurrentPage(page);
  }, []);

  const handleFilter = useCallback(() => {
    setIsFilterModalOpen(true);
  }, []);

  const handleSearch = useCallback(async (query: string) => {
    setSearchLoading(true);
    try {
      if (!query.trim()) {
        // Réinitialiser les filtres - le useEffect déclenchera le fetch
        setCurrentPage(1);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/commandes?commande_nom=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Erreur lors de la recherche');
        throw new Error('Erreur lors de la recherche');
      }

      const data = await response.json();

      if (data.status === 'success') {
        setCommandes(data.data);
        setFilteredData(data.data);

        if (data.pagination) {
          setPagination({
            currentPage: data.pagination.current_page,
            lastPage: data.pagination.last_page,
            perPage: data.pagination.per_page,
            total: data.pagination.total
          });
        } else {
          setPagination({
            currentPage: 1,
            lastPage: Math.ceil(data.data.length / 100),
            perPage: 100,
            total: data.data.length
          });
        }
      } else {
        setCommandes([]);
        setFilteredData([]);
        setPagination({ currentPage: 1, lastPage: 1, perPage: 50, total: 0 });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setError('Une erreur est survenue lors de la recherche.');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  }, []);

  /**
   * Vérifie si des filtres sont actuellement actifs
   */
  const hasActiveFilters = useMemo(() => {
    const hasLotTechnique = !!activeFilters.lotTechnique;
    const hasLotGeographique = !!activeFilters.lotGeographique;
    const hasNomOperation = !!activeFilters.nomOperation;
    const hasEntite = !!activeFilters.entite;
    const hasCreateur = !!activeFilters.createur;
    const hasStatus = !!activeFilters.status;
    const hasType = !!(activeFilters.type && activeFilters.type !== 'Tous');
    const hasPrixMin = !!(activeFilters.prixRange && activeFilters.prixRange.min > 0);
    const hasPrixMax = !!(activeFilters.prixRange && activeFilters.prixRange.max > 0);
    const hasDateStart = !!(activeFilters.dateRange && activeFilters.dateRange.start);
    const hasDateEnd = !!(activeFilters.dateRange && activeFilters.dateRange.end);

    return (
      hasLotTechnique || hasLotGeographique || hasNomOperation || hasEntite ||
      hasCreateur || hasStatus || hasType || hasPrixMin || hasPrixMax ||
      hasDateStart || hasDateEnd
    );
  }, [activeFilters]);

  /**
   * Applique les filtres en appelant l'API
   */
  const handleFilterApply = async (filters: FilterOptions) => {
    // Fermer le modal immédiatement
    setIsFilterModalOpen(false);

    // Sauvegarder les filtres actifs
    setActiveFilters(filters);

    // Réinitialiser la page à 1 lors de l'application de nouveaux filtres
    setCurrentPage(1);

    // Charger les données avec les nouveaux filtres selon l'onglet actif
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchCommandesImporteesWithFilters(1, filters);
    } else if (TABS[activeTab] === 'Archive') {
      await fetchArchives({ page: 1 });
    } else {
      await fetchCommandesWithFilters(1, activeTab, filters);
    }
  };

  /**
   * Réinitialise tous les filtres et recharge les données
   */
  const handleClearFilters = async () => {
    setActiveFilters({});
    setCurrentPage(1);

    if (TABS[activeTab] === 'Paniers importés') {
      await fetchCommandesImporteesWithFilters(1, {});
    } else if (TABS[activeTab] === 'Archive') {
      await fetchArchives({ page: 1 });
    } else {
      await fetchCommandesWithFilters(1, activeTab, {});
    }
  };

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  const getActionRequiseCount = () => {
    return commandes.filter(cmd => cmd.status.action === true && cmd.source !== "BCI").length;
  };

  const notifications = [
    {
      tabIndex: TABS.indexOf('Tous'),
      count: actionRequisCount
    },
    {
      tabIndex: TABS.indexOf('Action requise'),
      count: actionRequisCount
    },
    {
      tabIndex: TABS.indexOf('Paniers importés'),
      count: paniersImportesCount
    }
  ].filter(n => n.tabIndex !== -1 && n.count > 0);

  // ============================================================================
  // COLONNES TABLEAU - ARCHIVES
  // ============================================================================

  const archiveColumns = useMemo(() => [
    {
      field: 'type',
      header: 'Type',
      renderCell: () => <div className="type-info">BC</div>
    },
    {
      field: 'date_commande',
      header: 'Date',
      renderCell: (value: any) => <div>{formatDate(value, false)}</div>
    },
    {
      field: 'panier',
      header: 'Nom',
      renderCell: (value: string, row: any) => (
        <div className="commande-nom text-truncate" title={row.panier || '-'}>{row.panier || '-'}</div>
      )
    },
    {
      field: 'organisme',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: any, row: any) => {
        const displayValue = row.organisme?.nom || (isClientView ? '-' : '-');
        return <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'total_ht',
      header: 'Total HT',
      renderCell: (value: number, row: any) => (
        <div className="montant-commande">
          {formatPrice(Number(row?.total_ht))}
        </div>
      )
    },
    {
      field: 'statut',
      header: 'Statut',
      renderCell: (value: string) => (
        <div className={`status-badge ${toCssClass(value)}`}>
          {value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'}
        </div>
      )
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: any) => (
        <Button
          value="PDF"
          variant="secondary"
          size="small"
          className="action-button"
          onClick={() => downloadArchivePDF(row.id)}
        />
      )
    }
  ], [isClientView]);

  // Helper pour exécuter les actions (déplacé avant columns pour éviter les problèmes de référence)
  const executeAction = useCallback((action: string, row: Commande) => {
    switch (action) {
      case 'reprendre': handleReprendre(row.id.toString()); break;
      case 'analyser': handleAnalyser(row.id.toString()); break;
      case 'candidater': handleCandidater(row.id.toString()); break;
      case 'signer': handleSigner(row.id.toString()); break;
      case 'export': handleExport(row.id.toString()); break;
      case 'download': handleDownload(row); break;
      case 'accepter': handleAccepter(row.id.toString()); break;
      case 'chiffrer': handleChiffrer(row.id.toString()); break;
      case 'valider': handleValider(row); break;
      case 'view':
        if (row.source === 'BCI') {
          navigate(`/commande-bci/${row.id}?tab=0`, { state: { action: 'view' } });
        }
        break;
      default: console.log(`Aucune action définie pour: ${action}`);
    }
  }, [navigate, handleReprendre, handleAnalyser, handleCandidater, handleSigner, handleExport, handleDownload, handleAccepter, handleChiffrer, handleValider]);

  // ============================================================================
  // COLONNES TABLEAU - COMMANDES
  // ============================================================================

  const columns = useMemo(() => [
    {
      field: 'id',
      header: 'N°',
      renderCell: (value: number, row: Commande) => {
        const displayValue = row.operation?.numero || value.toString();
        return <div className="date-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'created_at',
      header: 'Date',
      renderCell: (value: string) => {
        const date = new Date(value);
        const formattedDate = date.toLocaleDateString('fr-FR');
        return <div className="date-info text-truncate" title={formattedDate}>{formattedDate}</div>;
      }
    },
    {
      field: 'lot_technique',
      header: 'Lot Technique',
      renderCell: (value: any, row: any) => {
        const lotTechniqueName = value?.nom || '-';
        return <div className="lot-technique text-truncate" title={lotTechniqueName}>{row.operation?.id_lot_technique}</div>;
      }
    },
    {
      field: 'id_lot_departement',
      header: 'Dép',
      renderCell: (value: number) => (
        <div className="lot-geographique text-truncate" title={value?.toString()}>{value?.toString()}</div>
      )
    },
    {
      field: 'cree_par',
      header: 'Créé par',
      renderCell: (_value: string, row: Commande) => {
        const creePar = row?.operation?.cree_par || '-';
        return <div className="user-info text-truncate" title={creePar}>{creePar}</div>;
      }
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="commande-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'organisme_nom',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: string) => {
        let displayValue: string;
        if (value === 'Non défini' || !value) {
          displayValue = isClientView ? 'Prestataire non défini' : 'Client non défini';
        } else {
          displayValue = value;
        }
        return <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'prix_total',
      header: 'Total HT',
      renderCell: (value: number | any | null) => (
        <div className="montant-commande">
          {value !== undefined && value !== null ? formatPrice(parseFloat(value), 2) : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: (value: { nom: string, id: number, delai: number | null, action?: string, type_compte: string }, row: Commande) => {
        if (!value) return null;
        const statusClass = toCssClass(value?.nom);
        const actionClass = value?.action ? 'action-requise' : '';
        let displayNom = value?.nom;

        if (row?.source === 'MS') {
          if (value?.id === 6) displayNom = 'Refusé';
          if (value?.id === 11) displayNom = 'Accepté';
          if (value?.id === 12 && row?.expire_at) displayNom = 'Expiré';
        }

        if (row?.source === 'BC') {
          if (value?.id === 8) displayNom = 'Refusé';
          if (value?.id === 7) displayNom = 'Accepté';
        }

        return (
          <div className={`status-badge ${statusClass} ${actionClass}`} title={`Type: ${value?.type_compte}`}>
            {displayNom}
          </div>
        );
      }
    },
    {
      field: 'expire_at',
      header: 'Temps restant',
      renderCell: (value: string, row: Commande) => {
        if (!row) return null;

        // Pour les paniers importés (BCI), afficher la date de début d'opération
        if (row.source === 'BCI' && row.operation?.date_debut_operation) {
          const date = new Date(row.operation.date_debut_operation);
          const formattedDate = date.toLocaleDateString('fr-FR');
          return (
            <div className="date-info" title={formattedDate}>
              {formattedDate}
            </div>
          );
        }

        // Pour les autres (BC/MS), afficher le compte à rebours
        // Ne PAS afficher pour : 5, 6, 7, 8, 12 (terminés/refusés/expirés)
        if (row?.expire_at) {
          const excludedStatuses = [5, 6, 7, 8, 12];
          if (!excludedStatuses.includes(row?.status?.id)) {
            return <CompteARebourd targetDate={row?.expire_at} />;
          }
        }
        return null;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: Commande) => {
        if (!row || !row?.status) return null;

        const userRole = getUserRoleFromStorage();
        const actionMapping = getStatusActionMapping(row?.status?.id, userRole, row);

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {actionMapping?.showButton && (
              <Button
                value={actionMapping.buttonText}
                variant="secondary"
                size="small"
                onClick={() => executeAction(actionMapping.action, row)}
                className={`action-button ${toCssClass(row.status.nom)} ${row.status.action ? 'action-requise' : ''}`}
                disabled={actionMapping.disableButton}
              />
            )}
            {row?.source === 'BCI' && row?.status?.id === 11 && (
              <button
                className="delete-brouillon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBCI(row);
                }}
                title="Supprimer la commande"
              >
                ×
              </button>
            )}
          </div>
        );
      }
    }
  ], [isClientView, executeAction, handleDeleteBCI]);

  // Calcul du prix maximum pour les filtres
  const maxPrice = useMemo(() =>
    Math.max(...commandes.map(cmd => cmd.prix_total || 0))
    , [commandes]);

  // ============================================================================
  // FETCH FONCTIONS
  // ============================================================================

  const fetchArchives = async (params: { page?: number } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const queryParams = {
        ...params,
        per_page: '100',
        ...(params.page !== undefined && { page: params.page.toString() })
      };
      const query = new URLSearchParams(queryParams as Record<string, string>).toString();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive?${query}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        setDataArchive(result.data || []);
        setPagination({
          currentPage: result.pagination.current_page,
          lastPage: result.pagination.last_page,
          perPage: result.pagination.per_page,
          total: result.pagination.total
        });
      } else {
        console.error('Erreur API Archives:', result);
        setDataArchive([]);
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête archives annulée");
        return;
      }
      console.error('Erreur réseau Archives:', error);
      setDataArchive([]);
    }
  };

  /**
   * Récupère les commandes avec support des filtres API
   */
  const fetchCommandesWithFilters = async (
    page = 1,
    tab = activeTab,
    filters: Partial<FilterOptions> = {}
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Aucun token d'authentification trouvé");

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);

      // Construction des paramètres de base avec le mapping des onglets
      let baseQueryParams = `page=${page}&per_page=100`;

      if (tab !== 0 && tab >= 0 && tab < TABS.length) {
        const tabMapping = TAB_TO_STATUS_MAPPING[tab];
        if (tabMapping && tabMapping.filter) {
          baseQueryParams = `${tabMapping.filter}&page=${page}&per_page=100`;
        }
      }

      if (selectedType !== 'Tous') {
        baseQueryParams += `&type=${selectedType}`;
      }

      // Fusionner les paramètres de base avec les filtres
      const finalQueryString = mergeFiltersWithBaseParams(baseQueryParams, apiFilters);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes?${finalQueryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal
        }
      );

      if (controller.signal.aborted) return;

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData: ApiResponse = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Transformation des données avec la fonction utilitaire
      let processedData = transformCommandesData(responseData.data);

      // Appliquer les filtres locaux pour les champs non supportés par l'API backend
      if (filters.dateRange?.start && filters.dateRange?.end) {
        processedData = processedData.filter(cmd => {
          const cmdDate = new Date(cmd.created_at);
          return cmdDate >= filters.dateRange!.start! && cmdDate <= filters.dateRange!.end!;
        });
      }

      if (filters.createur) {
        processedData = processedData.filter(cmd =>
          cmd.operation?.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase()) ||
          cmd.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase())
        );
      }

      if (filters.entite) {
        processedData = processedData.filter(cmd =>
          cmd.organisme_nom === filters.entite ||
          cmd.operation?.organisme_nom === filters.entite
        );
      }

      setCommandes(processedData);
      setFilteredData(processedData);
      setPagination({
        currentPage: responseData.pagination?.current_page || 1,
        lastPage: responseData.pagination?.last_page || 1,
        perPage: responseData.pagination?.per_page || 50,
        total: responseData.pagination?.total || 0
      });

    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête commandes annulée");
        return;
      }
      console.error('Erreur lors du chargement des commandes:', error);
      setError(error.message || 'Une erreur est survenue');
      setCommandes([]);
      setFilteredData([]);
    }
  };

  /**
   * Fonction simplifiée pour charger les commandes sans filtres personnalisés
   */
  const fetchCommandes = async (page = 1, tab = activeTab) => {
    await fetchCommandesWithFilters(page, tab, activeFilters);
  };

  /**
   * Récupère les commandes importées (BCI) avec support des filtres API
   */
  const fetchCommandesImporteesWithFilters = async (
    page = 1,
    filters: Partial<FilterOptions> = {}
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Aucun token d'authentification trouvé");

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);
      const queryString = buildQueryString(apiFilters);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes-importees?${queryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal
        }
      );

      if (controller.signal.aborted) return;

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Transformation et tri des paniers importés
      let processedData = transformCommandesData(responseData.data);

      // Appliquer les filtres locaux pour les champs non supportés par l'API backend
      if (filters.dateRange?.start && filters.dateRange?.end) {
        processedData = processedData.filter(cmd => {
          const cmdDate = new Date(cmd.created_at);
          return cmdDate >= filters.dateRange!.start! && cmdDate <= filters.dateRange!.end!;
        });
      }

      if (filters.createur) {
        processedData = processedData.filter(cmd =>
          cmd.operation?.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase()) ||
          cmd.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase())
        );
      }

      if (filters.entite) {
        processedData = processedData.filter(cmd =>
          cmd.organisme_nom === filters.entite ||
          cmd.operation?.organisme_nom === filters.entite
        );
      }

      const sortedData = sortPaniersImportesByPriority(processedData);

      setCommandes(sortedData);
      setFilteredData(sortedData);
      setPagination({
        currentPage: responseData.pagination?.current_page || 1,
        lastPage: responseData.pagination?.last_page || 1,
        perPage: responseData.pagination?.per_page || 100,
        total: responseData.pagination?.total || 0
      });

    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête paniers importés annulée");
        return;
      }
      console.error('Erreur lors du chargement des paniers importés:', error);
      setError(error.message || 'Une erreur est survenue');
      setCommandes([]);
      setFilteredData([]);
    }
  };

  /**
   * Fonction simplifiée pour charger les commandes importées sans filtres
   */
  const fetchCommandesImportees = async (page = 1, tab = activeTab) => {
    await fetchCommandesImporteesWithFilters(page, activeFilters);
  };

  // Fonction unique pour charger les compteurs (Action requise + Paniers importés)
  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes-compteur`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (result.status === 'success' && result.data) {
        const count = result.data.count ?? 0;        // MS + BDC pour "Action requise"
        const countBDCI = result.data.count_bdci ?? 0; // BCI pour "Paniers importés"
        const totalCount = count + countBDCI;         // Total pour le badge menu

        setActionRequisCount(count);
        setPaniersImportesCount(countBDCI);
        setUnreadCommandes(totalCount);
        if (onNotificationUpdate) {
          onNotificationUpdate(totalCount);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des compteurs:', error);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Effet pour charger les compteurs une seule fois au montage
  useEffect(() => {
    fetchCounts();
  }, []);

  // Effet principal - chargement des données selon l'onglet actif
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);

      try {
        if (TABS[activeTab] === 'Archive') {
          await fetchArchives({ page: currentPage });
        } else if (TABS[activeTab] === 'Paniers importés') {
          await fetchCommandesImportees(currentPage, activeTab);
        } else {
          await fetchCommandes(currentPage, activeTab);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [activeTab, selectedType, currentPage]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <div className="commandes-header">
          <h1>Commandes</h1>

          <div className="commandes-actions">
            <div className="bc-absent-message">
              <p>Sur cette nouvelle interface, les bons de commandes sont obligatoirement initiés par l'acheteur.</p>
              <p>Le prestataire de rang 1 est ensuite sollicité pour éventuellement chiffrer le contenu.</p>
              <p>Il n'existe plus de système de code à transmettre.</p>
              <p>N'hésitez pas à regarder le guide d'utilisation, dans le menu assistance.</p>
            </div>
          </div>
        </div>

        <TabNav
          tabs={TABS as unknown as string[]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
          inactiveTabs={[]}
          inactiveTabsTooltips={{
            "Paniers importés": "Nous procédons à l'importation de l'ancienne base de cours. Merci de votre patience : l'opération nécessite encore quelques heures."
          }}
          rightAlignedTabs={['Archive', 'Paniers importés']}
        />

        <div className="button-group">
          <div className="button-filter-group">
            <SearchBar
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              placeholder="Trouver une commande"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="third"
              size="small"
              icon={ExportIcon ? <ExportIcon /> : null}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <div className="filter-button-container">
              <Button
                value="filtrer"
                variant={hasActiveFilters ? "primary" : "third"}
                size="small"
                icon={FilterIcon ? <FilterIcon /> : null}
                onClick={handleFilter}
              />
              {hasActiveFilters && (
                <button
                  className="filter-reset-button"
                  onClick={handleClearFilters}
                  title="Réinitialiser les filtres"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Chargement des commandes en cours...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <TableauDeBord
            data={TABS[activeTab] === 'Archive' ? dataArchive : filteredData}
            columns={TABS[activeTab] === 'Archive' ? archiveColumns : columns}
            onRowClick={handleRowClick}
            onSearch={handleSearch}
            typeFilters={true}
            onExport={() => handleExportCSV(filteredData, columns, `commandes-${new Date().toISOString().slice(0, 10)}.csv`)}
            isSelectable={true}
            showActionBar={true}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showActionsColumn={false}
            itemsPerPage={pagination.perPage}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            disableDefaultSort={TABS[activeTab] === 'Paniers importés'}
          />
        )}
      </section>

      {/* Modal de filtres */}
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={commandes}
        maxPrice={maxPrice}
        activeFilters={activeFilters}
      />

      {/* Modal lancer marché */}
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
      />

      {/* Modal Bon de Commande */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={false}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
      />

      {/* Modal de confirmation de suppression BCI */}
      <ValidationPopUp
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={deleteError ? handleCancelDelete : handleConfirmDelete}
        title="Supprimer le panier importé"
        text={deleteError || `Êtes-vous sûr de vouloir supprimer ce panier importé d'un montant de ${commandeToDelete ? formatPrice(commandeToDelete.prix_total || 0) : '0,00 €'} ? Cette action est irréversible.`}
        cancelLabel="Annuler"
        confirmLabel={deleteError ? "Fermer" : "Supprimer"}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default Commandes;
